import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useGamification } from "@/hooks/useGamification";
import { supabase } from "@/integrations/supabase/client";
import { MemoryCategory } from "@/lib/memoryCategories";

// Interface for memory and scope
interface Memory {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  version: number;
  category: MemoryCategory;
  enabled: boolean;
  workspace_id?: string | null;
  board_id?: string | null;
  folder_id?: string | null;
  tag_id?: string | null;
  project_id?: string | null;
}

interface MemoryScope {
  workspaceId?: string;
  boardId?: string;
  folderId?: string;
  tagId?: string;
  projectId?: string;
}

export const useMemories = (scope: MemoryScope, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const { awardPoints } = useGamification();

  const queryKey = ['memories', scope];

  // Fetch memories
  const { data: memories = [], isLoading } = useQuery<Memory[], Error>({
    queryKey,
    queryFn: async () => {
      let query = supabase.from("memories").select("*").order("enabled", { ascending: false }).order("created_at", { ascending: false });

      if (scope.workspaceId) query = query.eq("workspace_id", scope.workspaceId);
      else if (scope.boardId) query = query.eq("board_id", scope.boardId);
      else if (scope.folderId) query = query.eq("folder_id", scope.folderId);
      else if (scope.tagId) query = query.eq("tag_id", scope.tagId);
      else if (scope.projectId) query = query.eq("project_id", scope.projectId);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled,
  });

  // Create/Update memory
  const saveMemory = useMutation({
    mutationFn: async (variables: {
      memoryId?: string;
      content: string;
      category: MemoryCategory;
    }) => {
      const { memoryId, content, category } = variables;
      if (!content.trim()) {
        throw new Error("Memory content cannot be empty");
      }

      let response;
      if (memoryId) {
        response = await invoke("manage-memory", {
          body: { action: "update", memoryId, content: content.trim(), category },
          showErrorToast: true,
        });
      } else {
        const scopeData: Record<string, string> = {};
        if (scope.workspaceId) scopeData.workspaceId = scope.workspaceId;
        else if (scope.boardId) scopeData.boardId = scope.boardId;
        else if (scope.folderId) scopeData.folderId = scope.folderId;
        else if (scope.tagId) scopeData.tagId = scope.tagId;
        else if (scope.projectId) scopeData.projectId = scope.projectId;

        response = await invoke("manage-memory", {
          body: { action: "create", content: content.trim(), category, scope: scopeData },
          showErrorToast: true,
        });
      }

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (_, variables) => {
      if (!variables.memoryId) {
        awardPoints('memory');
        toast.success("Memory added");
      } else {
        toast.success("Memory updated");
      }
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete memory
  const deleteMemory = useMutation({
    mutationFn: async (memoryId: string) => {
      const { error } = await invoke("manage-memory", {
        body: { action: "delete", memoryId },
        showErrorToast: true,
        successMessage: "Memory deleted",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Toggle memory enabled status
  const toggleMemoryEnabled = useMutation({
    mutationFn: async ({ memoryId, currentEnabled }: { memoryId: string; currentEnabled: boolean }) => {
      const { error } = await invoke("manage-memory", {
        body: { action: "toggleEnabled", memoryId, enabled: !currentEnabled },
        showErrorToast: true,
        successMessage: currentEnabled ? "Memory disabled" : "Memory enabled",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Promote memory
  const promoteMemory = useMutation({
    mutationFn: async ({ memory, targetScope }: { memory: Memory; targetScope: "workspace" | "board" | "folder" }) => {
      let targetScopeId: string | undefined;
  
      if (targetScope === "workspace") targetScopeId = scope.workspaceId;
      else if (targetScope === "board") {
        if (scope.boardId) targetScopeId = scope.boardId;
        else if (scope.projectId) {
          const { data: projectData } = await supabase.from("projects").select("board_id").eq("id", scope.projectId).single();
          targetScopeId = projectData?.board_id;
        }
      } else if (targetScope === "folder") targetScopeId = scope.folderId;
  
      if (!targetScopeId) {
        throw new Error(`No ${targetScope} context available to promote to.`);
      }
  
      const { error } = await invoke("manage-memory", {
        body: { action: "promote", memoryId: memory.id, targetScope, targetScopeId },
        showErrorToast: true,
        successMessage: `Memory promoted to ${targetScope} level`,
      });
  
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  // Refine memory
  const refineMemory = useMutation({
    mutationFn: async (content: string) => {
      if (!content.trim()) {
        throw new Error("Enter some content to refine");
      }
      const { data, error } = await invoke("refine-memory", {
        body: { content: content.trim() }
      });
      if (error) {
        if (error.message?.includes("Rate limit")) toast.error("Rate limit exceeded. Please try again later.");
        else if (error.message?.includes("credits")) toast.error("AI credits depleted. Please add credits to continue.");
        else toast.error("Failed to refine memory");
        throw error;
      }
      return data.refinedContent;
    },
    onSuccess: () => {
      toast.success("Memory refined");
    }
  });

  return {
    memories,
    isLoading,
    saveMemory: saveMemory.mutateAsync,
    isSaving: saveMemory.isPending,
    deleteMemory: deleteMemory.mutate,
    isDeleting: deleteMemory.isPending,
    toggleMemoryEnabled: toggleMemoryEnabled.mutate,
    isToggling: toggleMemoryEnabled.isPending,
    promoteMemory: promoteMemory.mutate,
    isPromoting: promoteMemory.isPending,
    refineMemory: refineMemory.mutateAsync,
    isRefining: refineMemory.isPending,
  };
};
